import React from 'react';
import { FileCheck, Scale, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <Scale className="h-4 w-4 text-amber-500 mr-2" />
              <span className="text-sm text-amber-500 font-medium">Legal Terms & Conditions</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Terms of <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Service</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Last Updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-12 bg-slate-900/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-6 text-center">
                <FileCheck className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Service Agreement</h3>
                <p className="text-gray-400 text-sm">Legal terms governing service use</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-6 text-center">
                <Scale className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Rights & Obligations</h3>
                <p className="text-gray-400 text-sm">Your rights and our responsibilities</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="pt-6 text-center">
                <CheckCircle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Clear & Fair</h3>
                <p className="text-gray-400 text-sm">Transparent terms, no hidden clauses</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert max-w-none">
            <div className="space-y-12">
              {/* Introduction */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Agreement to Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  These Terms of Service ("Terms") govern your use of the Global Filing Protection® website and services. By accessing or using our services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our services.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  We reserve the right to modify these Terms at any time. Continued use of our services after changes constitutes acceptance of the modified Terms.
                </p>
              </div>

              {/* Services Description */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Description of Services</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Global Filing Protection® provides trademark registration and intellectual property services, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Trademark searches and availability assessments</li>
                  <li>Federal trademark application preparation and filing</li>
                  <li>USPTO office action responses</li>
                  <li>Trademark monitoring and maintenance services</li>
                  <li>Copyright registration services</li>
                  <li>Business formation assistance</li>
                  <li>Amazon Brand Registry enrollment</li>
                </ul>
                <div className="bg-amber-500/10 border border-white/20 rounded-lg p-4 mt-6">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 text-sm">
                      <strong className="text-white">Important Notice:</strong> Our services do not constitute legal advice. While our team includes experienced trademark professionals, we recommend consulting with a licensed attorney for specific legal matters or complex situations.
                    </p>
                  </div>
                </div>
              </div>

              {/* Account and Registration */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Account Registration and Use</h2>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-6 space-y-4">
                  <p className="text-gray-300">When you register for our services, you agree to:</p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Promptly update any information that becomes inaccurate</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Accept responsibility for all activities under your account</li>
                  </ul>
                  <p className="text-gray-300 mt-4">
                    We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
                  </p>
                </div>
              </div>

              {/* Payment Terms */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Payment Terms and Fees</h2>
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Our service fees are clearly stated on our website and include USPTO filing fees unless otherwise specified. Additional terms include:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Payment is required before filing your trademark application</li>
                    <li>All fees are non-refundable once services have been rendered</li>
                    <li>Government fees paid to the USPTO are non-refundable regardless of outcome</li>
                    <li>We accept major credit cards and electronic payments</li>
                    <li>Prices are subject to change with prior notice</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed mt-4">
                    In the event of application rejection by the USPTO due to our error, we will refund our service fees (excluding government fees).
                  </p>
                </div>
              </div>

              {/* User Obligations */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">User Obligations and Prohibited Conduct</h2>
                <p className="text-gray-300 mb-4">You agree not to:</p>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-6">
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Provide false or misleading information</li>
                    <li>Attempt to register trademarks you do not have rights to use</li>
                    <li>Use our services for any unlawful purpose</li>
                    <li>Interfere with or disrupt our services or servers</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Copy, modify, or distribute our proprietary content without permission</li>
                    <li>Engage in any activity that infringes on others' intellectual property rights</li>
                  </ul>
                </div>
              </div>

              {/* Intellectual Property */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Intellectual Property Rights</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  All content on our website, including text, graphics, logos, images, and software, is the property of Global Filing Protection® or its licensors and is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  You retain all rights to the trademark applications and materials you submit to us. We will not use your proprietary information for any purpose other than providing our services, except as required by law.
                </p>
              </div>

              {/* Disclaimers */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Disclaimers and Limitations</h2>
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-6 space-y-4">
                  <div>
                    <h3 className="text-white font-semibold mb-2">No Guarantee of Results</h3>
                    <p className="text-gray-300 text-sm">
                      While we strive for high success rates, we cannot guarantee that your trademark application will be approved by the USPTO. Approval depends on many factors beyond our control.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Service Availability</h3>
                    <p className="text-gray-300 text-sm">
                      We make reasonable efforts to maintain service availability but do not guarantee uninterrupted access. We are not liable for any downtime or service interruptions.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Third-Party Dependencies</h3>
                    <p className="text-gray-300 text-sm">
                      Our services depend on the USPTO and other third parties. We are not responsible for delays, errors, or issues caused by third-party agencies.
                    </p>
                  </div>
                </div>
              </div>

              {/* Liability */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Limitation of Liability</h2>
                <p className="text-gray-300 leading-relaxed">
                  To the maximum extent permitted by law, Global Filing Protection® shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits, revenue, or data, arising from your use of our services.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  Our total liability for any claim related to our services is limited to the amount you paid for the specific service giving rise to the claim.
                </p>
              </div>

              {/* Indemnification */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Indemnification</h2>
                <p className="text-gray-300 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Global Filing Protection®, its officers, employees, and agents from any claims, liabilities, damages, losses, or expenses arising from your use of our services, violation of these Terms, or infringement of any third-party rights.
                </p>
              </div>

              {/* Termination */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Termination</h2>
                <p className="text-gray-300 leading-relaxed">
                  We may terminate or suspend your access to our services immediately, without prior notice, for any violation of these Terms. Upon termination, your right to use our services will cease immediately.
                </p>
                <p className="text-gray-300 leading-relaxed mt-4">
                  Provisions that by their nature should survive termination will continue to apply, including intellectual property rights, disclaimers, indemnification, and limitations of liability.
                </p>
              </div>

              {/* Governing Law */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Governing Law and Dispute Resolution</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  These Terms are governed by the laws of the United States and the State of New York, without regard to conflict of law provisions.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Any disputes arising from these Terms or our services shall be resolved through binding arbitration in New York, NY, except that either party may seek injunctive relief in court for intellectual property matters.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-slate-900/60 border border-white/20 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-white mb-4">Questions About Terms</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="space-y-2 text-gray-300">
                  <p><strong className="text-white">Email:</strong> info@globalfilingprotection.com</p>
                  <p><strong className="text-white">Phone:</strong> +1-833-202-0812</p>
                  <p><strong className="text-white">Address:</strong> 12100 Wilshire Blvd 8th floor, Los Angeles, CA 90025</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
